import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { FontAwesomeIcon } from '@hivebrite/design-system/FontAwesomeIcon';
import { toast } from '@hivebrite/design-system/Toast';

import DeleteDiscussionModal from 'hb-react/frontoffice/apps/Forum/components/DeleteDiscussionModal';
import { CLASS_NAME } from 'hb-react/frontoffice/apps/Forum/constants';
import {
  type Discussion,
  type DiscussionCard,
  type ConfigUrls,
} from 'hb-react/frontoffice/apps/Forum/types';
import ReportContent from 'hb-react/frontoffice/components/ReportContent';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import copyToClipboard from 'hb-react/shared/utils/copyToClipboard';

import DropdownButton from './DropdownButton';

interface ActionsDropdownProps {
  configUrls: Pick<ConfigUrls, 'discussionUrl' | 'editDiscussionUrl'>;
  discussion: Discussion | DiscussionCard;
}

const ActionsDropdown = ({ configUrls, discussion }: ActionsDropdownProps) => {
  const { currentUser } = useDefaultAppContexts();

  const { id, user } = discussion;

  const discussionUrl = `${window.location.origin}${configUrls.discussionUrl(
    id,
  )}`;

  if (currentUser?.id === user.id) {
    return (
      <CurrentUserDropdown
        configUrls={configUrls}
        discussion={discussion}
        discussionUrl={discussionUrl}
      />
    );
  }

  return (
    <OtherUserDropdown discussion={discussion} discussionUrl={discussionUrl} />
  );
};

interface CurrentUserDropdownProps extends ActionsDropdownProps {
  discussionUrl: string;
}

const CurrentUserDropdown = ({
  configUrls,
  discussion,
  discussionUrl,
}: CurrentUserDropdownProps) => {
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { id, title } = discussion;

  const onClickEdit = () => {
    navigate(configUrls.editDiscussionUrl(id));
  };

  return (
    <>
      <DropdownButton
        title={title}
        items={[
          {
            label: s__('Forum|Copy link'),
            icon: () => <FontAwesomeIcon iconName="copy" height="15px" />,
            dataTestId: 'copy-link-as-current-user',
            onClick: () =>
              copyToClipboard(discussionUrl, () =>
                toast.success(__('Link copied to clipboard')),
              ),
          },
          {
            label: __('Edit'),
            icon: () => <FontAwesomeIcon iconName="pen" height="15px" />,
            dataTestId: 'editing-discussion-as-current-user',
            onClick: onClickEdit,
          },
          {
            label: __('Delete'),
            icon: () => <FontAwesomeIcon iconName="trash-can" height="15px" />,
            dataTestId: 'deleting-discussion-as-current-user',
            onClick: () => setShowDeleteModal(true),
          },
        ]}
      />
      {showDeleteModal && (
        <DeleteDiscussionModal
          onCloseModal={() => setShowDeleteModal(false)}
          title={title}
          discussionId={id}
        />
      )}
    </>
  );
};

interface OtherUserDropdownProps {
  discussion: Discussion | DiscussionCard;
  discussionUrl: string;
}

const OtherUserDropdown = ({
  discussion,
  discussionUrl,
}: OtherUserDropdownProps) => {
  const { currentNetwork } = useDefaultAppContexts();
  const [showReportingModal, setShowReportingModal] = useState(false);

  const { id, title } = discussion;

  const items = [
    {
      label: s__('Forum|Copy link'),
      icon: () => <FontAwesomeIcon iconName="copy" height="15px" />,
      dataTestId: 'copy-link',
      onClick: () =>
        copyToClipboard(discussionUrl, () =>
          toast.success(__('Link copied to clipboard')),
        ),
    },
  ];

  const canReport =
    currentNetwork?.network_config?.user_can_report_user_contents;

  if (canReport) {
    items.push({
      label: __('Report this content'),
      icon: () => (
        <FontAwesomeIcon iconName="circle-exclamation" height="15px" />
      ),
      dataTestId: 'reporting-content-of-discussion',
      onClick: () => setShowReportingModal(true),
    });
  }

  return (
    <>
      <DropdownButton items={items} title={title} />
      {showReportingModal && (
        <ReportContent
          onCloseModal={() => setShowReportingModal(false)}
          id={id}
          type={CLASS_NAME}
        />
      )}
    </>
  );
};

export default ActionsDropdown;
